<template>
	<div class="file-page page pt-5">
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<div v-else-if="isBlocked !== null">
			<v-skeleton-loader
				style="max-width: 555px"
				v-bind="attrs"
				type="card"
				v-if="this.isLoading"
			>
			</v-skeleton-loader>
			<div class="file-page__body" v-else-if="resource">
				<div class="file-page__body-header">
					<SponsorBadge
						v-if="resource.sponsored_by || resource.pin_to_top"
						:text="resource.sponsored_by || pinned"
						:top="'0px'"
						:right="'0px'"
						:iconSize="10"
						:iconColor="'#ffffff'"
						:icon="resource.sponsored_by ? 'star' : 'pinned'"
					/>
					<div v-if="resourceImage" class="file-page__body-header-img-wrapper">
						<img
							:src="resource.resource_image"
							class="file-page__body-header-img mr-3"
							alt="resource image"
						/>
					</div>
					<div v-else class="file-page__body-header-icon-wrapper pl-2 mr-3">
						<ResourceFileIcon
							:fileName="resource.upload.file_real_name"
							:url="resource.upload.url"
							backgroundColor="#ffffff"
						/>
					</div>
					<div class="file-page__body-header__name-wrapper">
						<p class="file-page__info-filename">
							{{ resource.resources_name }}
						</p>
						<p class="file-page__info-author" v-if="resource.resource_author">
							{{ resource.resource_author }}
						</p>
					</div>
					<div class="d-flex align-end justify-end" style="flex-grow: 1">
						<a
							@click="toggleLink(resource.upload.url)"
							class="text-decoration-none"
							target="_self"
						>
							<button type="button" class="d-flex align-end">
								<Icon
									type="folder"
									:size="24"
									color="var(--button-main-background-color) "
									:pr="8"
								/>
								<span class="file-page__info-btn">Open</span>
							</button>
						</a>
					</div>
				</div>
				<div class="file-page__body-content mt-2">
					<div class="file-page__body-chips" v-if="resource.tags.length">
						<div
							class="file-page__body__tags"
							v-for="tag in resource.tags"
							:key="tag.tag_id"
						>
							{{ '#' + tag.tag }}
						</div>
					</div>
					<p v-if="resource.resource_text" class="file-page__text">
						{{ resource.resource_text }}
					</p>
					<div class="file-page__dates" v-if="datesSectionVisible">
						<p v-if="resourceUploadDate">
							<span class="file-page__dates-title"> Uploaded: </span>
							<span class="file-page__dates-value">
								{{ resourceUploadDate }}
							</span>
						</p>
						<v-divider
							v-if="resourceUploadDate && resourceReleaseDate"
							class="my-2"
							style="border-color: #f1f1f1"
						/>
						<p v-if="resourceReleaseDate">
							<span class="file-page__dates-title"> Release date: </span>
							<span class="file-page__dates-value">
								{{ resourceReleaseDate }}
							</span>
						</p>
						<v-divider
							v-if="resourceReleaseDate && resourceExpiryDate"
							class="my-2"
							style="border-color: #f1f1f1"
						/>
						<p v-if="resourceExpiryDate">
							<span class="file-page__dates-title"> Expiry date: </span>
							<span class="file-page__dates-value">
								{{ resourceExpiryDate }}
							</span>
						</p>
					</div>
				</div>
			</div>
			<div class="file-page__error" v-else-if="errors">
				{{ errors.message }}
			</div>
		</div>
	</div>
</template>

<script>
import ResourceFileIcon from '../components/Resource/ResourceFileIcon.vue'
import Block from '@/mixins/Block.js'
import linkifyThreadText from '@/mixins/linkifyThreadText'
import PageBlocked from '@/components/PageBlocked'
import { mapActions, mapGetters } from 'vuex'
import SponsorBadge from '@/components/SponsorBadge.vue'

export default {
	mixins: [Block, linkifyThreadText],
	components: {
		SponsorBadge,
		ResourceFileIcon,
		PageBlocked,
	},
	data() {
		return {
			attrs: {
				class: 'mb-6 mt-6',
				elevation: 0,
			},
			tags: ['Mobile', 'Member Engagement'],
			fileColors: [
				{ color: '#50bee8', ext: ['jpg', 'jpeg', 'psd'] },
				{ color: '#6678aa', ext: ['bmp'] },
				{ color: '#a066aa', ext: ['png', 'gif'] },
				{ color: '#f15642', ext: ['pdf'] },
				{ color: '#50bee8', ext: ['doc', 'docx'] },
				{ color: '#638674', ext: ['rtf'] },
				{ color: '#f15642', ext: ['ppt'] },
				{ color: '#84bd5a', ext: ['xls', 'xlsx'] },
				{ color: '#f7b84e', ext: ['mp3', 'wav', 'flac'] },
				{ color: '#f7b84e', ext: ['avi', 'mp4', 'mpeg'] },
				{ color: '#576d7e', ext: ['txt'] },
				{ color: '#e59f2b', ext: ['zip', 'rar', '7z', 'gz', 'gzip', 'bzip'] },
				{ color: '#41a8e1', ext: ['url'] },
			],
			pageBlockedName: 'resources',
		}
	},
	computed: {
		...mapGetters('resources', ['resource', 'isLoading', 'errors']),
		pinned() {
			return 'pinned'
		},

		resourceImage() {
			return this.resource.resource_image
		},
		resourceUploadDate() {
			if (this.resource.upload && this.resource.upload.time) {
				return this.dateCreated(this.resource.upload.time)
			}
			return ''
		},
		resourceReleaseDate() {
			if (this.resource.resource_release_date) {
				return this.formatDate(this.resource.resource_release_date)
			}
			return ''
		},
		resourceExpiryDate() {
			if (this.resource.resource_expiry_date) {
				return this.formatDate(this.resource.resource_expiry_date)
			}
			return ''
		},
		datesSectionVisible() {
			return this.resourceUploadDate || this.resourceReleaseDate || this.resourceExpiryDate
		},
	},
	methods: {
		...mapActions('resources', ['getResourceById', 'clearResource']),
		...mapActions('app', ['setPageTitle']),
    async toggleLink(link) {
      const target = link

      if (!target) return

      if (this.isUnsupportedDeepLink(target)) {
        this.mobileLink = true
        return
      }

      if (this.isMapUrlValid(target)) {
        try {
          await this.getMapRegisterToken()
          if (!this.mapRegisterToken) {
            console.error('Token not received')
            return
          }
          const urlWithToken = `${target}${target.includes('?') ? '&' : '?'}code=${
              this.mapRegisterToken
          }`
          window.open(urlWithToken, '_self')
        } catch (e) {
          console.error(e)
        }
      } else {
        window.open(target, '_self')
      }
    },

		dateCreated(originalDate) {
			const date = new Date(originalDate * 1000)
			const options = { year: 'numeric', month: 'short', day: 'numeric' }
			return date.toLocaleDateString('en-US', options)
		},
		formatDate(payload) {
			const date = new Date(payload * 1000)
			const options = {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			}
			const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date)
			return formattedDate
		},
	},
	mounted() {
		this.setPageTitle('Details')
	},
	created() {
		this.getResourceById(this.$route.query.category_id)
	},
	beforeDestroy() {
		this.setPageTitle('')
		this.clearResource()
	},
}
</script>

<style lang="scss">
.file-page {
	max-width: 600px;
	width: 100%;
	border-radius: 10px !important;
	margin-bottom: 30px;
	font-family: Montserrat, sans-serif;

	@media (max-width: 480px) {
		margin-bottom: 12px;
	}

	&__title {
		font-size: 24px;
		font-weight: 700;
		line-height: 29.26px;
		color: #4a4a4a;
	}

	&__body {
		&__tags {
			border: 1px solid #e2e2e2;
			border-radius: 20px;
			padding: 4px 8px;
			font-family: 'Inter', sans-serif;
			font-weight: 400;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
			width: fit-content;
			color: #4a4a4a;
		}
		&-header {
			display: flex;
			padding: 12px;
			background-color: #ffffff;
			border-radius: 8px;
			border: 1px solid #e2e2e2;
			position: relative;

			@media (max-width: 480px) {
				flex-direction: column;
			}
			&__name-wrapper {
				display: flex;
				flex-direction: column;
				justify-content: center;
				gap: 11px;
			}

			&-img {
				width: 120px;
				height: 120px;
				border-radius: 8px;
				object-fit: contain;

				&-wrapper {
					margin-right: 8px;
					display: flex;
					justify-content: center;
					align-items: center;

					@media (max-width: 480px) {
						margin-bottom: 8px;
						justify-content: flex-start;
					}
				}
			}

			&-icon {
				&-wrapper {
					width: 150px;
					height: 150px;
					min-width: 150px;
					border-radius: 8px;
					background-color: #f3f3f6;
					display: flex;
					align-items: center;
					justify-content: center;

					img {
						mix-blend-mode: multiply;
					}

					@media (max-width: 480px) {
						margin-bottom: 12px;
					}
				}
			}
		}

		&-content {
			background-color: #ffffff;
			padding: 12px;
			border: 1px solid #e2e2e2;
			border-radius: 8px;
		}
	}

	&__info {
		&-filename {
			color: var(--accent-color);
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 24px;
			line-height: 100%;
			letter-spacing: 0;
			white-space: normal;
			word-break: break-word;
		}

		&-author {
			font-family: 'Inter', sans-serif;
			font-weight: 400;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--username-category-title);
		}

		&-btn {
			color: var(--button-main-background-color) !important;
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
		}
	}

	&__text {
		font-family: 'Inter', sans-serif;
		font-weight: 400;
		font-size: 14px;
		line-height: 100%;
		letter-spacing: 0;
		margin-bottom: 12px !important;
	}

	&__dates {
		p {
			display: flex;
			justify-content: space-between;
			align-items: center;
			flex-wrap: nowrap;
			margin: 0;
		}

		&-title {
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
		}

		&-value {
			font-family: 'Inter', sans-serif;
			font-weight: 400;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: right;
			color: #9b9b9b;
		}
	}

	&__body-chips {
		display: flex;
		gap: 4px;
		margin-bottom: 12px;
	}

	&__error {
		color: red;
	}
}
</style>
